import axios from "axios";
import {
  EPLET_CATALOG,
  IMPUTATION_MAP,
  ALLELE_EPLET_MAP,
} from "../data/epletRegistryData";

/**
 * Client-Side Eplet Analysis & API Bridge
 */
export class EpletClientService {
  /**
   * Normalizes allele strings, auto-imputing 2-digit to 4-digit if needed.
   */
  static normalizeAllele(raw) {
    if (!raw || typeof raw !== "string") return null;
    let clean = raw.trim().toUpperCase().replace(/^HLA-/, "");

    if (/^[A-Z0-9]+\*\d{2}:\d{2}/.test(clean)) {
      return clean;
    }

    if (IMPUTATION_MAP[clean]) {
      return IMPUTATION_MAP[clean];
    }

    const locusPart = clean.split(":")[0];
    if (IMPUTATION_MAP[locusPart]) {
      return IMPUTATION_MAP[locusPart];
    }

    return clean;
  }

  static extractAllelesFromTyping(hlaTyping) {
    if (!hlaTyping || typeof hlaTyping !== "object") return [];
    const alleles = [];

    for (const [key, value] of Object.entries(hlaTyping)) {
      if (key === "_id" || key === "createdAt" || key === "updatedAt" || key === "__v") continue;
      if (typeof value === "string" && value.trim() && value !== "N/A" && value !== "-") {
        const normalized = this.normalizeAllele(value);
        if (normalized) alleles.push(normalized);
      }
    }
    return [...new Set(alleles)];
  }

  static getEpletsForAlleles(alleleList) {
    const epletMap = new Map();

    for (const allele of alleleList) {
      const normalized = this.normalizeAllele(allele);
      const epletIds = ALLELE_EPLET_MAP[normalized] || [];

      for (const epletId of epletIds) {
        if (EPLET_CATALOG[epletId]) {
          epletMap.set(epletId, {
            ...EPLET_CATALOG[epletId],
            sourceAllele: normalized,
            pHLA3DUrl: `https://www.epregistry.com.br/index/eplets/3d/?eplet=${encodeURIComponent(epletId)}`,
          });
        }
      }
    }

    return epletMap;
  }

  /**
   * Performs fast in-browser molecular eplet mismatch calculation.
   */
  static calculateMismatchClientSide(recipientInput, donorInput, recipientAntibodies = []) {
    const recipientAlleles = Array.isArray(recipientInput)
      ? recipientInput.map((a) => this.normalizeAllele(a)).filter(Boolean)
      : this.extractAllelesFromTyping(recipientInput);

    const donorAlleles = Array.isArray(donorInput)
      ? donorInput.map((a) => this.normalizeAllele(a)).filter(Boolean)
      : this.extractAllelesFromTyping(donorInput);

    const recipientEplets = this.getEpletsForAlleles(recipientAlleles);
    const donorEplets = this.getEpletsForAlleles(donorAlleles);

    const antibodyList = Array.isArray(recipientAntibodies) ? recipientAntibodies : [];
    const reactiveAntigenMap = new Map();
    for (const ab of antibodyList) {
      const antigen = (ab.antigen || ab.allele || "").trim().toUpperCase();
      const mfi = typeof ab.MFI === "number" ? ab.MFI : parseFloat(ab.mfi || ab.MFI || 0);
      if (antigen && !isNaN(mfi) && mfi > 1000) {
        reactiveAntigenMap.set(antigen, mfi);
      }
    }

    const mismatchedEplets = [];
    let confirmedCount = 0;
    let theoreticalCount = 0;
    let classICount = 0;
    let classIICount = 0;
    let drb1Count = 0;
    let dqb1Count = 0;
    let dpb1Count = 0;
    let hasHighMfiEpletDsa = false;
    let maxMatchedMfi = 0;

    for (const [epletId, epletInfo] of donorEplets.entries()) {
      if (!recipientEplets.has(epletId)) {
        let matchedMfi = null;
        let isDsa = false;

        for (const [antigen, mfi] of reactiveAntigenMap.entries()) {
          const normAntigen = this.normalizeAllele(antigen);
          if (
            normAntigen === epletInfo.sourceAllele ||
            epletInfo.alleles.includes(normAntigen) ||
            epletInfo.sourceAllele.startsWith(normAntigen)
          ) {
            matchedMfi = Math.max(matchedMfi || 0, mfi);
            isDsa = true;
          }
        }

        if (matchedMfi && matchedMfi > maxMatchedMfi) {
          maxMatchedMfi = matchedMfi;
        }
        if (matchedMfi && matchedMfi > 10000) {
          hasHighMfiEpletDsa = true;
        }

        mismatchedEplets.push({
          ...epletInfo,
          isDsa,
          matchedMfi,
        });

        if (epletInfo.evidence === "Confirmed") confirmedCount++;
        else theoreticalCount++;

        if (epletInfo.locus === "ABC") classICount++;
        else {
          classIICount++;
          if (epletInfo.locus === "DRB") drb1Count++;
          if (epletInfo.locus === "DQ") dqb1Count++;
          if (epletInfo.locus === "DP") dpb1Count++;
        }
      }
    }

    let riskTier = "LOW";
    let riskTierLabel = "Low Risk (Safe)";
    let riskTierColor = "green";
    let dnDsaPrediction = "Minimal (<3% at 5 years)";

    if (hasHighMfiEpletDsa) {
      riskTier = "CONTRAINDICATED";
      riskTierLabel = "Unfit - Preformed Eplet DSA > 10,000 MFI";
      riskTierColor = "red";
      dnDsaPrediction = "Extreme Risk - Immediate AMR Risk";
    } else if (drb1Count > 10 || dqb1Count > 14 || classICount > 16) {
      riskTier = "HIGH";
      riskTierLabel = "High Molecular Risk (Prone to dnDSA)";
      riskTierColor = "red";
      dnDsaPrediction = "High (28-35% at 5 years)";
    } else if (drb1Count >= 7 || dqb1Count >= 9 || classICount >= 10) {
      riskTier = "INTERMEDIATE";
      riskTierLabel = "Intermediate Molecular Load";
      riskTierColor = "yellow";
      dnDsaPrediction = "Moderate (~12% at 5 years)";
    }

    let epletScore = 100;
    if (hasHighMfiEpletDsa) {
      epletScore = 0;
    } else {
      const penalty =
        (Math.min(classICount, 25) / 25) * 40 +
        (Math.min(drb1Count, 15) / 15) * 30 +
        (Math.min(dqb1Count, 20) / 20) * 30;
      epletScore = Math.max(0, Math.round(100 - penalty));
    }

    let inductionRecommendation =
      "Standard IL-2 receptor antagonist (Basiliximab 20 mg on Days 0 and 4) is adequate. T-cell depletion (rATG) avoided.";
    let cniTarget =
      "Safe for controlled CNI sparing / moderate Tacrolimus trough (5-7 ng/mL at 6 months). Rapid steroid withdrawal feasible.";
    let surveillance =
      "Routine Luminex Single Antigen Bead (SAB) monitoring at 3 months, 12 months, and annually.";

    if (riskTier === "HIGH") {
      inductionRecommendation =
        "Mandatory T-cell depleting induction (rATG / Thymoglobulin 4.5-6.0 mg/kg total over 3-5 days).";
      cniTarget =
        "Strict CNI adherence (Tacrolimus trough 8-10 ng/mL through Month 6, >= 7 ng/mL long-term). Triple immunosuppression indefinite.";
      surveillance =
        "Intensive Luminex surveillance at 1, 2, 3, 6, 9, 12 months post-op. Protocol biopsy at 6 or 12 months recommended.";
    } else if (riskTier === "INTERMEDIATE") {
      inductionRecommendation =
        "Tailor induction based on secondary clinical factors; consider low-dose rATG (3 mg/kg) or Basiliximab.";
      cniTarget =
        "Standard Tacrolimus trough (7-9 ng/mL) with MMF (1.5-2.0 g/day) and steroid maintenance.";
      surveillance = "Luminex screening at 1, 3, 6, and 12 months post-transplant.";
    }

    return {
      recipientAlleles,
      donorAlleles,
      totalMismatches: mismatchedEplets.length,
      confirmedMismatches: confirmedCount,
      theoreticalMismatches: theoreticalCount,
      classI: classICount,
      classII: classIICount,
      drb1: drb1Count,
      dqb1: dqb1Count,
      dpb1: dpb1Count,
      riskTier,
      riskTierLabel,
      riskTierColor,
      dnDsaPrediction,
      epletScore,
      hasHighMfiEpletDsa,
      maxMatchedMfi,
      recommendations: {
        induction: inductionRecommendation,
        cni: cniTarget,
        surveillance,
      },
      mismatchedEplets: mismatchedEplets.sort((a, b) => {
        if (a.isDsa && !b.isDsa) return -1;
        if (!a.isDsa && b.isDsa) return 1;
        if (a.evidence === "Confirmed" && b.evidence !== "Confirmed") return -1;
        if (a.evidence !== "Confirmed" && b.evidence === "Confirmed") return 1;
        return (b.exposition || 0) - (a.exposition || 0);
      }),
    };
  }

  // In-memory cache for computed eplet analyses
  static _analysisCache = new Map();
  static _inFlightAnalysis = new Map();

  /**
   * Fetches eplet analysis with in-memory caching and immediate client-side computation.
   * If clinical data is already present, computes in <1ms without any network request.
   */
  static async fetchAnalysis(recipientId, donorId, token, fallbackClinicalPair = null) {
    const key = `${recipientId || "none"}_${donorId || "none"}`;

    // 1. Check cache
    if (this._analysisCache.has(key)) {
      return this._analysisCache.get(key);
    }

    // 2. If clinical pair is already provided, compute client-side instantly (0 network latency)
    if (fallbackClinicalPair) {
      const recTyping = fallbackClinicalPair.recipient_clinical?.hlaTyping;
      const donTyping = fallbackClinicalPair.donor_clinical?.hlaTyping;
      const recAbs = fallbackClinicalPair.recipient_clinical?.hlaAntibody || [];
      if (recTyping && donTyping) {
        const result = this.calculateMismatchClientSide(recTyping, donTyping, recAbs);
        this._analysisCache.set(key, result);
        return result;
      }
    }

    // 3. Deduplicate in-flight network requests
    if (this._inFlightAnalysis.has(key)) {
      return this._inFlightAnalysis.get(key);
    }

    const requestPromise = (async () => {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api";
      try {
        const response = await axios.post(
          `${API_URL}/eplet/analyze`,
          { recipient_id: recipientId, donor_id: donorId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.success && response.data?.data) {
          const data = response.data.data;
          this._analysisCache.set(key, data);
          return data;
        }
      } catch (err) {
        console.warn("[EpletClientService] Backend eplet API call note:", err.message);
      } finally {
        this._inFlightAnalysis.delete(key);
      }
      return null;
    })();

    this._inFlightAnalysis.set(key, requestPromise);
    return requestPromise;
  }
}

export default EpletClientService;
